import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { queueRequest, isNetworkError } from '../utils/offlineQueue';
import { Clock, Plus, Edit2, Trash2, Search, Info, Calendar, CloudRain, CloudSnow, Sun, Umbrella, Users, User, Bell } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toETInputValue, etInputToUTC, todayET } from '../utils/timezone';
import WeeklyHoursReminderDialog from '../components/WeeklyHoursReminderDialog';
import VoiceTextarea from '../components/VoiceTextarea';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const TimeClock = ({ user }) => {
  const [projects, setProjects] = useState([]);
  const [timeLogs, setTimeLogs] = useState([]);
  const [viewMode, setViewMode] = useState('week');
  const [adminView, setAdminView] = useState('my'); // 'my' or 'all'
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('all'); // For filtering specific employee
  const [sortBy, setSortBy] = useState('recent'); // 'recent', 'oldest', 'client'
  const [isManualEntryOpen, setIsManualEntryOpen] = useState(false);
  const [isRainDayOpen, setIsRainDayOpen] = useState(false);
  const [isSnowDayOpen, setIsSnowDayOpen] = useState(false);
  const [isStatHolidayOpen, setIsStatHolidayOpen] = useState(false);
  const [isHolidayOpen, setIsHolidayOpen] = useState(false);
  const [isReminderOpen, setIsReminderOpen] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [allStaff, setAllStaff] = useState([]); // All employees, supervisors, and admins
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedForExport, setSelectedForExport] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [manualEntry, setManualEntry] = useState({
    clock_in: '',
    clock_out: '',
    project_id: '',
    service_id: '',
    service_type: '',
    client_id: '',
    other_client: '',
    notes: '',
    no_lunch: false,
    worked_alone: false,
    worked_with: [],
    // Barge log fields (only used when service is Barging)
    barge_type: 'S',
    location: '',
    fuel_added: '',
    ramp_used: '',
    operating_time: '',
    standby_time: '',
    platform_time: '',
    equipment_skidsteer_hrs: '',
    equipment_excavator_hrs: '',
    equipment_other_hrs: '',
    equipment_other_name: '',
    load_description: '',
    persons_on_barge: ''
  });
  const [clients, setClients] = useState([]);
  const [submittingClient, setSubmittingClient] = useState(false);
  const [newClientDialogOpen, setNewClientDialogOpen] = useState(false);
  const [newClientForm, setNewClientForm] = useState({ name: '', company: '', phone: '', note: '' });
  const [services, setServices] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [entrySort, setEntrySort] = useState(() => localStorage.getItem('timeclock-entry-sort') || 'recent');

  const recentsKey = `timeclock-recents-${user?.id}`;
  const getRecents = () => {
    try { return JSON.parse(localStorage.getItem(recentsKey)) || {}; } catch { return {}; }
  };
  const recordRecents = (entry) => {
    const r = getRecents();
    const ts = Date.now();
    r.projects = r.projects || {}; r.clients = r.clients || {}; r.services = r.services || {};
    if (entry.project_id && entry.project_id !== 'none') r.projects[entry.project_id] = ts;
    if (entry.client_id && entry.client_id !== 'none' && entry.client_id !== 'other') r.clients[entry.client_id] = ts;
    if (entry.service_type && entry.service_type !== 'none') r.services[entry.service_type] = ts;
    localStorage.setItem(recentsKey, JSON.stringify(r));
  };
  const changeEntrySort = (mode) => {
    setEntrySort(mode);
    localStorage.setItem('timeclock-entry-sort', mode);
  };
  const sortOptions = (list, getName, getKey, recentMap) => {
    const sorted = [...list].sort((a, b) => getName(a).localeCompare(getName(b)));
    if (entrySort !== 'recent') return sorted;
    return sorted.sort((a, b) => (recentMap[getKey(b)] || 0) - (recentMap[getKey(a)] || 0));
  };
  const recents = getRecents();
  const sortedProjects = sortOptions(projects, (p) => p.name || '', (p) => p.id, recents.projects || {});
  const sortedClients = sortOptions(clients, (c) => c.name || '', (c) => c.id, recents.clients || {});
  const sortedServiceTypes = sortOptions(serviceTypes, (t) => t, (t) => t, recents.services || {});
  const [myBargeLogs, setMyBargeLogs] = useState([]);

  const fetchMyBargeLogs = async () => {
    try {
      const response = await axios.get(`${API}/barge-logs/mine`);
      setMyBargeLogs(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setMyBargeLogs([]);
    }
  };

  const fetchServiceTypes = async () => {
    try {
      const response = await axios.get(`${API}/settings/service-types`);
      setServiceTypes(response.data.service_types || []);
    } catch (error) {
      setServiceTypes([]);
    }
  };
  const [rainDayEntry, setRainDayEntry] = useState({
    date: '',
    project_id: '',
    notes: ''
  });
  const [snowDayEntry, setSnowDayEntry] = useState({
    date: '',
    project_id: '',
    notes: ''
  });
  const [holidayEntry, setHolidayEntry] = useState({
    date: '',
    project_id: '',
    notes: ''
  });
  const [statHolidayEntry, setStatHolidayEntry] = useState({
    date: '',
    project_id: '',
    notes: ''
  });

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchProjects(),
        fetchTimeLogs(),
        fetchTeamMembers(),
        fetchClients(),
        fetchServices(),
        fetchServiceTypes(),
        fetchMyBargeLogs()
      ]);
      setLoading(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchTimeLogs();
    }
  }, [viewMode]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      if (Array.isArray(response.data)) {
        setProjects(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      setProjects([]);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/team-members`);
      if (Array.isArray(response.data)) {
        setTeamMembers(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch team members:', error);
      setTeamMembers([]);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      if (Array.isArray(response.data)) {
        // Filter only client users (exclude pending — they aren't approved yet)
        const clientUsers = response.data.filter(u =>
          u.role === 'client' && u.approval_status !== 'pending'
        );
        setClients(clientUsers);
        
        // Also set all staff (employees, supervisors, admins) for admin view
        const staffUsers = response.data.filter(u => 
          u.role === 'employee' || u.role === 'supervisor' || u.role === 'admin'
        );
        setAllStaff(staffUsers);
      }
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      setClients([]);
      setAllStaff([]);
    }
  };

  const submitClientForApproval = async () => {
    const name = (manualEntry.other_client || '').trim();
    if (!name) {
      toast.error('Type a client name first');
      return;
    }
    setSubmittingClient(true);
    try {
      await axios.post(`${API}/clients/suggest`, { name });
      toast.success('Submitted! Walter has been notified. The client will appear in the list once approved.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit client for approval');
    } finally {
      setSubmittingClient(false);
    }
  };

  const submitNewClientDialog = async () => {
    const name = (newClientForm.name || '').trim();
    if (!name) {
      toast.error('Client name is required');
      return;
    }
    setSubmittingClient(true);
    try {
      await axios.post(`${API}/clients/suggest`, {
        name,
        company: newClientForm.company.trim() || null,
        phone: newClientForm.phone.trim() || null,
        note: newClientForm.note.trim() || null,
      });
      toast.success('Submitted! Walter has been notified. The client will appear in the list once approved.');
      // Auto-fill the manual entry with the typed name so the timesheet still records it
      setManualEntry(prev => ({ ...prev, client_id: 'other', other_client: name }));
      setNewClientForm({ name: '', company: '', phone: '', note: '' });
      setNewClientDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit client for approval');
    } finally {
      setSubmittingClient(false);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      if (Array.isArray(response.data)) {
        setServices(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch services:', error);
      setServices([]);
    }
  };

  const fetchTimeLogs = async () => {
    try {
      const response = await axios.get(`${API}/timelog`);
      if (Array.isArray(response.data)) {
        setTimeLogs(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch time logs:', error);
      setTimeLogs([]);
    }
  };

  const handleSaveEntry = async () => {
    if (!manualEntry.clock_in || !manualEntry.clock_out) {
      toast.error('Please fill in all required fields');
      return;
    }

    const hasProject = manualEntry.project_id && manualEntry.project_id !== 'none';
    const hasClient = (manualEntry.client_id && manualEntry.client_id !== 'none' && manualEntry.client_id !== 'other') ||
      (manualEntry.client_id === 'other' && manualEntry.other_client.trim());
    if (!hasProject && !hasClient) {
      toast.error('Please choose a project or a client before adding the entry');
      return;
    }

    // Validate barge log fields if it's a barging service
    if (isBarging()) {
      if (!manualEntry.location || !manualEntry.load_description || !manualEntry.persons_on_barge) {
        toast.error('Please fill in all required barge log fields (Location, Load Description, Persons on Barge)');
        return;
      }
    }

    let entryData = null;
    try {
      // Convert datetime-local format (interpreted as ET) to UTC ISO format
      const clockInISO = etInputToUTC(manualEntry.clock_in);
      const clockOutISO = etInputToUTC(manualEntry.clock_out);

      entryData = {
        clock_in: clockInISO,
        clock_out: clockOutISO,
        project_id: manualEntry.project_id === 'none' ? null : manualEntry.project_id,
        service_id: manualEntry.service_id === 'none' ? null : manualEntry.service_id,
        service_type: !manualEntry.service_type || manualEntry.service_type === 'none' ? null : manualEntry.service_type,
        client_id: manualEntry.client_id === 'none' || manualEntry.client_id === 'other' ? null : manualEntry.client_id,
        other_client: manualEntry.other_client,
        notes: manualEntry.notes,
        worked_with: manualEntry.worked_alone ? [] : manualEntry.worked_with,
        no_lunch: !!manualEntry.no_lunch,
        worked_alone: !!manualEntry.worked_alone
      };

      // Add barge log data if it's a barging service
      if (isBarging()) {
        entryData.barge_log = {
          date: manualEntry.clock_in.split('T')[0],
          barge_type: manualEntry.barge_type,
          location: manualEntry.location,
          client: manualEntry.other_client || clients.find(c => c.id === manualEntry.client_id)?.name || '',
          fuel_added: manualEntry.fuel_added,
          ramp_used: manualEntry.ramp_used,
          start_time: manualEntry.clock_in.split('T')[1],
          finish_time: manualEntry.clock_out.split('T')[1],
          operating_time: manualEntry.operating_time || calculateDuration()?.replace(/[hm\s]/g, ''),
          standby_time: manualEntry.standby_time,
          platform_time: manualEntry.platform_time,
          equipment_skidsteer_hrs: manualEntry.equipment_skidsteer_hrs,
          equipment_excavator_hrs: manualEntry.equipment_excavator_hrs,
          equipment_other_hrs: manualEntry.equipment_other_hrs,
          equipment_other_name: manualEntry.equipment_other_name,
          load_description: manualEntry.load_description,
          persons_on_barge: manualEntry.persons_on_barge,
          comments: manualEntry.notes // Use the same notes for comments
        };
      }

      if (editingLog) {
        await axios.put(`${API}/timelog/${editingLog.id}`, entryData);
        toast.success('Time entry updated!');
      } else {
        await axios.post(`${API}/timelog/manual-entry`, entryData);
        toast.success('Time entry added!');
      }
      recordRecents(manualEntry);
      
      setIsManualEntryOpen(false);
      setEditingLog(null);
      setManualEntry({
        clock_in: '',
        clock_out: '',
        project_id: '',
        service_id: '',
        service_type: '',
        client_id: '',
        other_client: '',
        notes: '',
        no_lunch: false,
        worked_alone: false,
        worked_with: [],
        barge_type: 'S',
        location: '',
        fuel_added: '',
        ramp_used: '',
        operating_time: '',
        standby_time: '',
        platform_time: '',
        equipment_skidsteer_hrs: '',
        equipment_excavator_hrs: '',
        equipment_other_hrs: '',
        equipment_other_name: '',
        load_description: '',
        persons_on_barge: ''
      });
      fetchTimeLogs();
    } catch (error) {
      if (!editingLog && entryData && isNetworkError(error)) {
        await queueRequest({ url: `${API}/timelog/manual-entry`, method: 'POST', jsonBody: entryData, description: 'Time entry' });
        recordRecents(manualEntry);
        toast.success('No signal — time entry saved on this device and will sync automatically');
        setIsManualEntryOpen(false);
        return;
      }
      console.error('Time entry error:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to save entry');
    }
  };

  const handleSaveRainDay = async () => {
    if (!rainDayEntry.date) {
      toast.error('Please select a date');
      return;
    }

    const rainDayData = {
      date: rainDayEntry.date,
      project_id: rainDayEntry.project_id === 'none' ? null : rainDayEntry.project_id,
      notes: rainDayEntry.notes || 'Rain Day - No work'
    };

    try {
      await axios.post(`${API}/timelog/rain-day`, rainDayData);
      toast.success('Rain day added successfully');
      setIsRainDayOpen(false);
      fetchTimeLogs();
    } catch (error) {
      if (isNetworkError(error)) {
        await queueRequest({ url: `${API}/timelog/rain-day`, method: 'POST', jsonBody: rainDayData, description: 'Rain day' });
        toast.success('No signal — rain day saved on this device and will sync automatically');
        setIsRainDayOpen(false);
        return;
      }
      console.error('Error adding rain day:', error);
      toast.error(error.response?.data?.detail || 'Failed to add rain day');
    }
  };

  const handleSaveSnowDay = async () => {
    if (!snowDayEntry.date) {
      toast.error('Please select a date');
      return;
    }

    const snowDayData = {
      date: snowDayEntry.date,
      project_id: snowDayEntry.project_id === 'none' ? null : snowDayEntry.project_id,
      notes: snowDayEntry.notes || 'Snow Day - No work'
    };

    try {
      await axios.post(`${API}/timelog/snow-day`, snowDayData);
      toast.success('Snow day added successfully');
      setIsSnowDayOpen(false);
      fetchTimeLogs();
    } catch (error) {
      if (isNetworkError(error)) {
        await queueRequest({ url: `${API}/timelog/snow-day`, method: 'POST', jsonBody: snowDayData, description: 'Snow day' });
        toast.success('No signal — snow day saved on this device and will sync automatically');
        setIsSnowDayOpen(false);
        return;
      }
      console.error('Error adding snow day:', error);
      toast.error(error.response?.data?.detail || 'Failed to add snow day');
    }
  };

  const handleSaveStatHoliday = async () => {
    if (!statHolidayEntry.date) {
      toast.error('Please select a date');
      return;
    }

    try {
      const payload = {
        date: statHolidayEntry.date,
        project_id: statHolidayEntry.project_id === 'none' ? null : statHolidayEntry.project_id,
        notes: statHolidayEntry.notes || 'Stat Holiday - No work'
      };

      await axios.post(`${API}/timelog/stat-holiday`, payload);
      toast.success('Stat holiday added successfully');
      setIsStatHolidayOpen(false);
      fetchTimeLogs();
    } catch (error) {
      console.error('Error adding stat holiday:', error);
      toast.error(error.response?.data?.detail || 'Failed to add stat holiday');
    }
  };

  const handleSaveHoliday = async () => {
    if (!holidayEntry.date) {
      toast.error('Please select a date');
      return;
    }

    try {
      const payload = {
        date: holidayEntry.date,
        project_id: holidayEntry.project_id === 'none' ? null : holidayEntry.project_id,
        notes: holidayEntry.notes || 'Holiday - No work'
      };

      await axios.post(`${API}/timelog/holiday`, payload);
      toast.success('Holiday added successfully');
      setIsHolidayOpen(false);
      fetchTimeLogs();
    } catch (error) {
      console.error('Error adding holiday:', error);
      toast.error(error.response?.data?.detail || 'Failed to add holiday');
    }
  };

  const handleEditLog = (log) => {
    const clockInLocal = toETInputValue(log.clock_in);
    const clockOutLocal = toETInputValue(log.clock_out);
    
    setManualEntry({
      clock_in: clockInLocal,
      clock_out: clockOutLocal,
      project_id: log.project_id || 'none',
      service_id: log.service_id || '',
      service_type: log.service_type || '',
      client_id: log.client_id || '',
      other_client: log.other_client || '',
      notes: log.notes || '',
      no_lunch: !!log.no_lunch,
      worked_alone: !!log.worked_alone,
      worked_with: log.worked_with || []
    });
    setEditingLog(log);
    setIsManualEntryOpen(true);
  };

  const handleDeleteLog = async (logId) => {
    if (!window.confirm('Delete this time entry?')) return;
    
    try {
      await axios.delete(`${API}/timelog/${logId}`);
      toast.success('Entry deleted!');
      fetchTimeLogs();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const toggleWorkedWith = (memberId) => {
    setManualEntry(prev => ({
      ...prev,
      worked_with: prev.worked_with.includes(memberId)
        ? prev.worked_with.filter(id => id !== memberId)
        : [...prev.worked_with, memberId]
    }));
  };

  const calculateDuration = () => {
    if (!manualEntry.clock_in || !manualEntry.clock_out) return null;
    
    const start = new Date(manualEntry.clock_in);
    const end = new Date(manualEntry.clock_out);
    
    if (isNaN(start) || isNaN(end) || end <= start) return null;
    
    const diffMs = end - start;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m`;
  };

  const isBarging = () => {
    if (manualEntry.service_type === 'Barging') return true;
    const selectedService = services.find(s => s.id === manualEntry.service_id);
    return selectedService?.service_type === 'Barging';
  };

  const toggleSelectForExport = (logId) => {
    setSelectedForExport(prev => 
      prev.includes(logId)
        ? prev.filter(id => id !== logId)
        : [...prev, logId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedForExport.length === filteredLogs.length) {
      setSelectedForExport([]);
    } else {
      setSelectedForExport(filteredLogs.map(log => log.id));
    }
  };

  const handleExportToEmail = () => {
    if (selectedForExport.length === 0) {
      toast.error('Please select at least one entry to export');
      return;
    }

    const sourceLogs = timeLogs.filter(log => selectedForExport.includes(log.id));

    // Sort by clock_in so earliest is Entry 1 and latest is Entry N
    const selectedLogs = [...sourceLogs].sort(
      (a, b) => new Date(a.clock_in) - new Date(b.clock_in)
    );
    const earliest = selectedLogs[0]?.clock_in;
    const latest = selectedLogs[selectedLogs.length - 1]?.clock_in;
    const fmtDate = (d) => d
      ? new Date(d).toLocaleDateString('en-US', { timeZone: 'America/Toronto', month: 'short', day: 'numeric', year: 'numeric' })
      : '';
    const dateRange = earliest && latest
      ? (earliest === latest || fmtDate(earliest) === fmtDate(latest)
          ? fmtDate(earliest)
          : `${fmtDate(earliest)} – ${fmtDate(latest)}`)
      : '';

    // Format date range for subject
    const employeeName = (user && user.name) ? user.name : 'Employee';
    const subject = dateRange
      ? `${employeeName} Dawal Time Sheet - ${dateRange}`
      : `${employeeName} Dawal Time Sheet`;

    // Format email body
    let body = `Time Entries Report\n`;
    if (dateRange) body += `Date Range: ${dateRange}\n`;
    body += `Total Hours: ${calculateTotalHours(selectedLogs)}\n`;
    body += `Total Entries: ${selectedLogs.length}\n`;
    const noLunchDays = [...new Set(selectedLogs.filter(l => l.no_lunch).map(l =>
      new Date(l.clock_in).toLocaleDateString('en-US', { timeZone: 'America/Toronto', weekday: 'short', month: 'short', day: 'numeric' })
    ))];
    if (noLunchDays.length > 0) {
      body += `No Lunch Taken: ${noLunchDays.join(', ')}\n`;
    }
    body += `\n`;
    body += `${'='.repeat(60)}\n\n`;

    // Group entries by date (in Eastern Time)
    const dayKey = (iso) => new Date(iso).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
    const groups = new Map(); // key = YYYY-MM-DD, value = { label, logs }
    selectedLogs.forEach((log) => {
      const k = dayKey(log.clock_in);
      if (!groups.has(k)) {
        const label = new Date(log.clock_in).toLocaleDateString('en-US', {
          timeZone: 'America/Toronto',
          weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
        });
        groups.set(k, { label, logs: [] });
      }
      groups.get(k).logs.push(log);
    });

    // Build body — one block per date, multiple entries listed under it.
    // Entry numbers are GLOBAL (continuous across the whole export) so each
    // entry keeps its sequential number while still being grouped by day.
    const sortedKeys = [...groups.keys()].sort();
    let entryCounter = 0;
    sortedKeys.forEach((k) => {
      const { label, logs } = groups.get(k);
      // Sum hours for the day (skip 0-hour rain/snow/stat entries from the total label
      // but still list them so the user can see them)
      const dayHours = logs.reduce((sum, l) => sum + (l.hours || 0), 0);
      body += `${label}\n`;
      body += `${logs.length} ${logs.length === 1 ? 'entry' : 'entries'} • ${dayHours.toFixed(1)}h total\n`;
      body += `${'-'.repeat(60)}\n`;

      logs.forEach((log) => {
        entryCounter += 1;
        const projectName = getProjectName(log.project_id);
        const serviceName = log.service_type || (log.service_id ? services.find(s => s.id === log.service_id)?.service_type : null);
        const clientName = log.client_id ? clients.find(c => c.id === log.client_id)?.name : null;

        body += `\n  Entry ${entryCounter}:\n`;
        if (clientName) body += `    Client: ${clientName}\n`;
        if (projectName && projectName !== 'No Project') {
          body += `    Project: ${projectName}\n`;
        }
        if (serviceName) {
          body += `    Service: ${serviceName}\n`;
        }
        // Show "Rain/Snow/Stat Holiday" label instead of times for zero-hour entries
        if (log.is_rain_day) {
          body += `    Type: Rain Day (no work)\n`;
        } else if (log.is_snow_day) {
          body += `    Type: Snow Day (no work)\n`;
        } else if (log.is_stat_holiday) {
          body += `    Type: Stat Holiday (no work)\n`;
        } else if (log.is_holiday) {
          body += `    Type: Holiday (no work)\n`;
        } else {
          body += `    Time: ${formatTime(log.clock_in)} – ${log.clock_out ? formatTime(log.clock_out) : 'In Progress'}\n`;
          body += `    Hours: ${log.hours}\n`;
          if (log.no_lunch) body += `    No Lunch Taken\n`;
        }

        if (log.worked_with && log.worked_with.length > 0) {
          const teamNames = log.worked_with.map(memberId => {
            const member = teamMembers.find(m => m.id === memberId);
            return member?.name || 'Unknown';
          }).join(', ');
          body += `    Worked With: ${teamNames}\n`;
        }
        if (log.worked_alone) {
          body += `    Worked Alone\n`;
        }

        if (log.notes) {
          // Indent multi-line notes
          const indented = String(log.notes).split('\n').map(l => `      ${l}`).join('\n');
          body += `    Notes:\n${indented}\n`;
        }

        // Attach barge sheet if this entry has one
        const barge = myBargeLogs.find(b => b.time_log_id === log.id);
        if (barge) {
          body += `    --- Barge Sheet ---\n`;
          body += `    Date: ${barge.date || ''}   Barge Type: ${barge.barge_type || ''}\n`;
          body += `    Location: ${barge.location || ''}\n`;
          if (barge.client) body += `    Client: ${barge.client}\n`;
          body += `    Start: ${barge.start_time || ''}   Finish: ${barge.finish_time || ''}\n`;
          if (barge.operating_time) body += `    Operating Time: ${barge.operating_time}\n`;
          if (barge.standby_time) body += `    Standby Time: ${barge.standby_time}\n`;
          if (barge.platform_time) body += `    Platform Time: ${barge.platform_time}\n`;
          if (barge.fuel_added) body += `    Fuel Added: ${barge.fuel_added}\n`;
          if (barge.ramp_used) body += `    Ramp Used: ${barge.ramp_used}\n`;
          if (barge.equipment_skidsteer_hrs) body += `    Skidsteer Hrs: ${barge.equipment_skidsteer_hrs}\n`;
          if (barge.equipment_excavator_hrs) body += `    Excavator Hrs: ${barge.equipment_excavator_hrs}\n`;
          if (barge.equipment_other_hrs) body += `    ${barge.equipment_other_name || 'Other Equip'} Hrs: ${barge.equipment_other_hrs}\n`;
          body += `    Load: ${barge.load_description || ''}\n`;
          body += `    Persons on Barge: ${barge.persons_on_barge || ''}\n`;
          if (barge.comments) body += `    Comments: ${barge.comments}\n`;
          body += `    -------------------\n`;
        }
      });

      body += `\n${'='.repeat(60)}\n\n`;
    });

    // Create mailto link
    const mailtoLink = `mailto:${user?.email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    // Open email client
    window.location.href = mailtoLink;
    
    // Clear selection after export
    setSelectedForExport([]);
    toast.success('Opening your email client...');
  };

  const getFilteredLogs = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    const filtered = timeLogs.filter(log => {
      const logDate = new Date(log.clock_in);
      
      // If custom date range is set, use that instead of viewMode
      if (startDate && endDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        return logDate >= start && logDate <= end;
      }
      
      // Otherwise, use viewMode filtering
      switch(viewMode) {
        case 'day':
          return logDate >= today && logDate < new Date(today.getTime() + 24 * 60 * 60 * 1000);
        case 'week': {
          // Monday → Sunday of current week (not a rolling 7-day window)
          const dow = today.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
          const daysSinceMonday = (dow + 6) % 7; // Mon=0, Tue=1, ..., Sun=6
          const monday = new Date(today);
          monday.setDate(today.getDate() - daysSinceMonday);
          monday.setHours(0, 0, 0, 0);
          const nextMonday = new Date(monday);
          nextMonday.setDate(monday.getDate() + 7);
          return logDate >= monday && logDate < nextMonday;
        }
        case 'month':
          const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
          return logDate >= monthAgo;
        default:
          return true;
      }
    });
    
    // Sort by clock_in date - newest first (descending order)
    return filtered.sort((a, b) => {
      const dateA = new Date(a.clock_in);
      const dateB = new Date(b.clock_in);
      return dateB - dateA; // Descending order (newest first)
    });
  };

  const groupLogsByDate = (logs) => {
    if (!Array.isArray(logs)) return {};
    
    const groups = {};
    logs.forEach(log => {
      try {
        const date = new Date(log.clock_in);
        const dateKey = date.toLocaleDateString('en-US', { timeZone: 'America/Toronto', weekday: 'short', 
          month: 'short', 
          day: 'numeric',
          year: 'numeric' });
        if (!groups[dateKey]) {
          groups[dateKey] = [];
        }
        groups[dateKey].push(log);
      } catch (e) {
        console.error('Error grouping log:', e);
      }
    });
    return groups;
  };

  const groupLogsByEmployee = (logs) => {
    if (!Array.isArray(logs)) return {};
    
    const groups = {};
    logs.forEach(log => {
      const employeeName = log.user_name || 'Unknown User';
      if (!groups[employeeName]) {
        groups[employeeName] = {
          logs: [],
          totalHours: 0
        };
      }
      groups[employeeName].logs.push(log);
      groups[employeeName].totalHours += log.hours || 0;
    });
    return groups;
  };

  const getSearchFilteredLogs = (logs) => {
    if (!searchQuery.trim() || !Array.isArray(logs)) return logs;
    
    const query = searchQuery.toLowerCase();
    return logs.filter(log => {
      try {
        const projectName = projects.find(p => p.id === log.project_id)?.name || 'No Project';
        const teamNames = (log.worked_with || []).map(id => {
          const member = teamMembers.find(m => m.id === id);
          return member?.name || '';
        }).join(' ');
        
        return (
          projectName.toLowerCase().includes(query) ||
          (log.notes || '').toLowerCase().includes(query) ||
          teamNames.toLowerCase().includes(query)
        );
      } catch (e) {
        return false;
      }
    });
  };

  const calculateTotalHours = (logs) => {
    if (!Array.isArray(logs)) return '0.00';
    return logs.reduce((total, log) => {
      return total + (parseFloat(log.hours) || 0);
    }, 0).toFixed(2);
  };

  const formatTime = (dateString) => {
    try {
      return new Date(dateString).toLocaleTimeString('en-US', { timeZone: 'America/Toronto', hour: 'numeric',
        minute: '2-digit',
        hour12: true });
    } catch (e) {
      return 'Invalid time';
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', { timeZone: 'America/Toronto', month: 'short',
        day: 'numeric',
        year: 'numeric' });
    } catch (e) {
      return 'Invalid date';
    }
  };

  const getProjectName = (projectId) => {
    if (!projectId) return 'No Project';
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : 'No Project';
  };

  const getLogClientName = (log) => {
    if (log.client_id) {
      const c = clients.find(cl => cl.id === log.client_id);
      if (c) return c.name;
    }
    return log.other_client || null;
  };

  const getLogServiceName = (log) => {
    if (log.service_type) return log.service_type;
    if (log.service_id) {
      const s = services.find(sv => sv.id === log.service_id);
      if (s) return s.service_type || s.name;
    }
    return null;
  };

  const getEntryTitle = (log) => {
    if (log.project_id) return getProjectName(log.project_id);
    return getLogClientName(log) || 'No Project';
  };

  const getClientName = (projectId) => {
    if (!projectId) return 'No Client';
    const project = projects.find(p => p.id === projectId);
    if (!project) return 'No Client';
    const client = clients.find(c => c.id === project.client_id);
    return client ? client.name : 'No Client';
  };

  const getEmployeeSummaries = (logs) => {
    const summaries = {};
    
    // First, add all staff members (employees, supervisors, admins) with 0 hours
    allStaff.forEach(member => {
      summaries[member.id] = {
        user_id: member.id,
        user_name: member.name,
        total_hours: 0,
        entries_count: 0
      };
    });
    
    // Then, update with actual log data for those who have logs
    logs.forEach(log => {
      if (!summaries[log.user_id]) {
        // This handles users not in allStaff (like deleted users showing as "Unknown User")
        summaries[log.user_id] = {
          user_id: log.user_id,
          user_name: log.user_name || 'Unknown User',
          total_hours: 0,
          entries_count: 0
        };
      }
      summaries[log.user_id].total_hours += log.hours || 0;
      summaries[log.user_id].entries_count += 1;
    });
    
    return Object.values(summaries).sort((a, b) => b.total_hours - a.total_hours);
  };

  const sortLogs = (logs) => {
    const sorted = [...logs];
    if (sortBy === 'recent') {
      return sorted.sort((a, b) => new Date(b.clock_in) - new Date(a.clock_in));
    } else if (sortBy === 'oldest') {
      return sorted.sort((a, b) => new Date(a.clock_in) - new Date(b.clock_in));
    } else if (sortBy === 'client') {
      return sorted.sort((a, b) => {
        const clientA = getClientName(a.project_id);
        const clientB = getClientName(b.project_id);
        return clientA.localeCompare(clientB);
      });
    }
    return sorted;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  let filteredLogs = getSearchFilteredLogs(getFilteredLogs());
  
  // Filter by selected employee if in admin view
  if (adminView === 'all' && selectedEmployeeId !== 'all') {
    filteredLogs = filteredLogs.filter(log => log.user_id === selectedEmployeeId);
  }
  
  // Sort logs based on selected sort option
  filteredLogs = sortLogs(filteredLogs);
  
  const groupedLogs = (user.role === 'admin' || user.role === 'supervisor') && adminView === 'all' 
    ? groupLogsByEmployee(filteredLogs) 
    : groupLogsByDate(filteredLogs);
  const totalHours = calculateTotalHours(filteredLogs);
  const isEmployeeView = (user.role === 'admin' || user.role === 'supervisor') && adminView === 'all';
  const employeeSummaries = isEmployeeView ? getEmployeeSummaries(getSearchFilteredLogs(getFilteredLogs())) : [];

  return (
    <div className="min-h-screen bg-gray-100 pb-20 pt-16 sm:pt-20">
      {/* Header */}
      <div className="bg-white border-b-2 border-gray-300 shadow-md">
        <div className="w-full px-4 py-4">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-blue-600" />
              <h1 className="text-2xl font-bold text-gray-900">Time Entries</h1>
            </div>
            <div className="flex gap-2 flex-wrap w-full sm:w-auto">
              <Dialog open={isManualEntryOpen} onOpenChange={setIsManualEntryOpen}>
              <DialogContent className="bg-white text-gray-900 border-2 border-gray-400 max-w-lg w-[95vw] max-h-[85vh] overflow-hidden flex flex-col shadow-xl">
                <DialogHeader className="flex-shrink-0 pb-3 border-b-2 border-gray-200">
                  <DialogTitle className="text-gray-900 text-2xl font-bold">{editingLog ? 'Edit Entry' : 'Add Entry'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-5 overflow-y-auto flex-1 pr-2 pt-4">
                  <div>
                    <Label className="text-gray-900 font-bold text-base mb-2 block">Start</Label>
                    <div className="space-y-2">
                      <Input
                        type="date"
                        value={manualEntry.clock_in ? manualEntry.clock_in.split('T')[0] : ''}
                        onChange={(e) => {
                          const currentTime = manualEntry.clock_in ? manualEntry.clock_in.split('T')[1] || '07:00' : '07:00';
                          setManualEntry({ ...manualEntry, clock_in: `${e.target.value}T${currentTime}` });
                        }}
                        className="text-gray-900 font-semibold border-2 border-gray-400 text-base h-12"
                      />
                      <Input
                        type="time"
                        value={manualEntry.clock_in ? manualEntry.clock_in.split('T')[1] || '' : ''}
                        onChange={(e) => {
                          const currentDate = manualEntry.clock_in ? manualEntry.clock_in.split('T')[0] : todayET();
                          setManualEntry({ ...manualEntry, clock_in: `${currentDate}T${e.target.value}` });
                        }}
                        className="text-gray-900 font-semibold border-2 border-gray-400 text-base h-12"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-900 font-bold text-base mb-2 block">End</Label>
                    <div className="space-y-2">
                      <Input
                        type="date"
                        value={manualEntry.clock_out ? manualEntry.clock_out.split('T')[0] : ''}
                        onChange={(e) => {
                          const currentTime = manualEntry.clock_out ? manualEntry.clock_out.split('T')[1] || '17:00' : '17:00';
                          setManualEntry({ ...manualEntry, clock_out: `${e.target.value}T${currentTime}` });
                        }}
                        className="text-gray-900 font-semibold border-2 border-gray-400 text-base h-12"
                      />
                      <Input
                        type="time"
                        value={manualEntry.clock_out ? manualEntry.clock_out.split('T')[1] || '' : ''}
                        onChange={(e) => {
                          const currentDate = manualEntry.clock_out ? manualEntry.clock_out.split('T')[0] : todayET();
                          setManualEntry({ ...manualEntry, clock_out: `${currentDate}T${e.target.value}` });
                        }}
                        className="text-gray-900 font-semibold border-2 border-gray-400 text-base h-12"
                      />
                    </div>
                  </div>
                  
                  {/* Duration Display */}
                  {calculateDuration() && (
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                      <Label className="text-blue-900 font-bold text-base mb-1 block">Duration</Label>
                      <p className="text-2xl font-bold text-blue-700">{calculateDuration()}</p>
                    </div>
                  )}
                  
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-gray-900 font-bold text-base">Project <span className="text-red-600 text-sm font-semibold">(required if no client)</span></Label>
                      <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => changeEntrySort('recent')}
                          className={`px-2.5 py-1 text-xs font-bold ${entrySort === 'recent' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`}
                          data-testid="entry-sort-recent-btn"
                        >
                          Recent
                        </button>
                        <button
                          type="button"
                          onClick={() => changeEntrySort('alpha')}
                          className={`px-2.5 py-1 text-xs font-bold ${entrySort === 'alpha' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`}
                          data-testid="entry-sort-alpha-btn"
                        >
                          A–Z
                        </button>
                      </div>
                    </div>
                    <Select
                      value={manualEntry.project_id}
                      onValueChange={(value) => setManualEntry({ ...manualEntry, project_id: value })}
                    >
                      <SelectTrigger className="mt-1 border-2 border-gray-400 text-gray-900 font-semibold text-base h-12">
                        <SelectValue placeholder="Select project" className="text-gray-900" />
                      </SelectTrigger>
                      <SelectContent className="text-gray-900 font-semibold">
                        <SelectItem value="none">No Project</SelectItem>
                        {sortedProjects.map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-900 font-bold text-base mb-2 block">Service (Optional)</Label>
                    <Select
                      value={manualEntry.service_type}
                      onValueChange={(value) => setManualEntry({ ...manualEntry, service_type: value, service_id: '' })}
                    >
                      <SelectTrigger className="mt-1 border-2 border-gray-400 text-gray-900 font-semibold text-base h-12">
                        <SelectValue placeholder="Select service" className="text-gray-900" />
                      </SelectTrigger>
                      <SelectContent className="text-gray-900 font-semibold">
                        <SelectItem value="none">No Service</SelectItem>
                        {sortedServiceTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-gray-900 font-bold text-base">Client <span className="text-red-600 text-sm font-semibold">(required if no project)</span></Label>
                      <Button
                        type="button"
                        onClick={() => setNewClientDialogOpen(true)}
                        className="bg-green-600 hover:bg-green-700 text-white text-xs font-bold h-8 px-3"
                        data-testid="open-new-client-dialog-btn"
                      >
                        + New Client
                      </Button>
                    </div>
                    <Select
                      value={manualEntry.client_id}
                      onValueChange={(value) => {
                        // Clear other_client if a client from list is selected
                        setManualEntry(prev => ({
                          ...prev,
                          client_id: value,
                          other_client: value === 'other' ? prev.other_client : ''
                        }));
                      }}
                    >
                      <SelectTrigger className="mt-1 border-2 border-gray-400 text-gray-900 font-semibold text-base h-12">
                        <SelectValue placeholder="Select client" className="text-gray-900" />
                      </SelectTrigger>
                      <SelectContent className="text-gray-900 font-semibold">
                        <SelectItem value="none">No Client</SelectItem>
                        {sortedClients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name} {client.company ? `(${client.company})` : ''}
                          </SelectItem>
                        ))}
                        <SelectItem value="other">Other (specify below)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Other Client Text Field */}
                  {manualEntry.client_id === 'other' && (
                    <div>
                      <Label className="text-gray-900 font-bold text-base mb-2 block">Other Client Name</Label>
                      <Input
                        type="text"
                        value={manualEntry.other_client}
                        onChange={(e) => setManualEntry(prev => ({ ...prev, other_client: e.target.value }))}
                        placeholder="Enter client name..."
                        className="text-gray-900 font-semibold border-2 border-gray-400 text-base h-12"
                        data-testid="other-client-name-input"
                      />
                      <div className="mt-2 bg-amber-50 border border-amber-200 rounded-md p-3">
                        <p className="text-xs text-amber-900 mb-2">
                          Client not in the list yet? Submit this name for admin approval — Walter will be notified by email and the client will appear in the dropdown once approved.
                        </p>
                        <Button
                          type="button"
                          onClick={submitClientForApproval}
                          disabled={submittingClient || !manualEntry.other_client.trim()}
                          className="bg-amber-600 hover:bg-amber-700 text-white font-bold text-sm h-9 px-3"
                          data-testid="submit-client-for-approval-btn"
                        >
                          {submittingClient ? 'Submitting…' : 'Submit for admin approval'}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Barge Log Fields - Only show for Barging service */}
                  {isBarging() && (
                    <div className="border-4 border-blue-300 rounded-lg p-4 bg-blue-50 space-y-4">
                      <div>
                        <h3 className="text-xl font-bold text-blue-900 mb-2">📋 Daily Barge Log Details</h3>
                        {(() => {
                          const selectedService = services.find(s => s.id === manualEntry.service_id);
                          const isAssigned = !selectedService?.assigned_employees || 
                                           selectedService.assigned_employees.length === 0 || 
                                           selectedService.assigned_employees.includes(user.id) ||
                                           manualEntry.service_type === 'Barging';
                          
                          if (!isAssigned) {
                            return (
                              <div className="bg-red-100 border-2 border-red-400 rounded p-3 mb-3">
                                <p className="text-red-900 font-semibold">⚠️ You are not assigned to fill barge logs for this service</p>
                                <p className="text-red-700 text-sm">Please contact your administrator to be assigned.</p>
                              </div>
                            );
                          }
                          
                          if (selectedService?.assigned_employees && selectedService.assigned_employees.length > 0) {
                            return (
                              <div className="bg-green-100 border-2 border-green-400 rounded p-2 mb-2">
                                <p className="text-green-900 font-semibold text-sm">✓ You are authorized to fill this barge log</p>
                              </div>
                            );
                          }
                          
                          return null;
                        })()}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <Label className="text-gray-900 font-bold text-sm">Barge Type *</Label>
                          <Select
                            value={manualEntry.barge_type}
                            onValueChange={(value) => setManualEntry(prev => ({ ...prev, barge_type: value }))}
                          >
                            <SelectTrigger className="border-2 border-gray-400 text-gray-900 font-semibold h-10">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="S">S</SelectItem>
                              <SelectItem value="L">L</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label className="text-gray-900 font-bold text-sm">Location *</Label>
                          <Input
                            value={manualEntry.location}
                            onChange={(e) => setManualEntry(prev => ({ ...prev, location: e.target.value }))}
                            className="border-2 border-gray-400 text-gray-900 font-semibold h-10"
                            placeholder="e.g., NARROWS"
                            required
                          />
                        </div>

                        <div>
                          <Label className="text-gray-900 font-bold text-sm">Ramp Used #</Label>
                          <Input
                            value={manualEntry.ramp_used}
                            onChange={(e) => setManualEntry(prev => ({ ...prev, ramp_used: e.target.value }))}
                            className="border-2 border-gray-400 text-gray-900 font-semibold h-10"
                            placeholder="Ramp #"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div>
                          <Label className="text-gray-900 font-bold text-sm">Fuel Added</Label>
                          <Input
                            value={manualEntry.fuel_added}
                            onChange={(e) => setManualEntry(prev => ({ ...prev, fuel_added: e.target.value }))}
                            className="border-2 border-gray-400 text-gray-900 font-semibold h-10"
                            placeholder="Gallons"
                          />
                        </div>

                        <div>
                          <Label className="text-gray-900 font-bold text-sm">Standby Time (hrs)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={manualEntry.standby_time}
                            onChange={(e) => setManualEntry(prev => ({ ...prev, standby_time: e.target.value }))}
                            className="border-2 border-gray-400 text-gray-900 font-semibold h-10"
                            placeholder="0.0"
                          />
                        </div>

                        <div>
                          <Label className="text-gray-900 font-bold text-sm">Platform Time (hrs)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={manualEntry.platform_time}
                            onChange={(e) => setManualEntry(prev => ({ ...prev, platform_time: e.target.value }))}
                            className="border-2 border-gray-400 text-gray-900 font-semibold h-10"
                            placeholder="0.0"
                          />
                        </div>

                        <div>
                          <Label className="text-gray-900 font-bold text-sm">Operating Time</Label>
                          <Input
                            value={calculateDuration() || manualEntry.operating_time}
                            readOnly
                            className="border-2 border-blue-400 text-blue-900 font-bold h-10 bg-blue-100"
                            placeholder="Auto"
                          />
                        </div>
                      </div>

                      <div className="bg-green-100 rounded p-3 space-y-3">
                        <h4 className="font-bold text-green-900">Equipment Used</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div>
                            <Label className="text-gray-900 font-bold text-sm">Skidsteer (hrs)</Label>
                            <Input
                              type="number"
                              step="0.1"
                              value={manualEntry.equipment_skidsteer_hrs}
                              onChange={(e) => setManualEntry(prev => ({ ...prev, equipment_skidsteer_hrs: e.target.value }))}
                              className="border-2 border-gray-400 text-gray-900 font-semibold h-10"
                              placeholder="0.0"
                            />
                          </div>

                          <div>
                            <Label className="text-gray-900 font-bold text-sm">Excavator (hrs)</Label>
                            <Input
                              type="number"
                              step="0.1"
                              value={manualEntry.equipment_excavator_hrs}
                              onChange={(e) => setManualEntry(prev => ({ ...prev, equipment_excavator_hrs: e.target.value }))}
                              className="border-2 border-gray-400 text-gray-900 font-semibold h-10"
                              placeholder="0.0"
                            />
                          </div>

                          <div>
                            <Label className="text-gray-900 font-bold text-sm">Other Equipment</Label>
                            <Input
                              value={manualEntry.equipment_other_name}
                              onChange={(e) => setManualEntry(prev => ({ ...prev, equipment_other_name: e.target.value }))}
                              className="border-2 border-gray-400 text-gray-900 font-semibold h-10"
                              placeholder="Name"
                            />
                          </div>

                          <div>
                            <Label className="text-gray-900 font-bold text-sm">Other (hrs)</Label>
                            <Input
                              type="number"
                              step="0.1"
                              value={manualEntry.equipment_other_hrs}
                              onChange={(e) => setManualEntry(prev => ({ ...prev, equipment_other_hrs: e.target.value }))}
                              className="border-2 border-gray-400 text-gray-900 font-semibold h-10"
                              placeholder="0.0"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-gray-900 font-bold text-sm">Load Description *</Label>
                          <Input
                            value={manualEntry.load_description}
                            onChange={(e) => setManualEntry(prev => ({ ...prev, load_description: e.target.value }))}
                            className="border-2 border-gray-400 text-gray-900 font-semibold h-10"
                            placeholder="e.g., SKID STEER, SIDING"
                            required
                          />
                        </div>

                        <div>
                          <Label className="text-gray-900 font-bold text-sm">Persons on Barge *</Label>
                          <Input
                            value={manualEntry.persons_on_barge}
                            onChange={(e) => setManualEntry(prev => ({ ...prev, persons_on_barge: e.target.value }))}
                            className="border-2 border-gray-400 text-gray-900 font-semibold h-10"
                            placeholder="e.g., ZACH, WALT"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {teamMembers.length > 0 && (
                    <div>
                      <Label className="mb-2 block text-gray-900 font-bold text-base">Worked With</Label>
                      <div
                        onClick={() => setManualEntry((prev) => ({ ...prev, worked_alone: !prev.worked_alone, worked_with: !prev.worked_alone ? [] : prev.worked_with }))}
                        className={`flex items-center gap-3 p-3 mb-2 rounded-lg border-2 cursor-pointer transition ${
                          manualEntry.worked_alone ? 'border-blue-600 bg-blue-50' : 'border-gray-300 bg-white'
                        }`}
                        data-testid="worked-alone-toggle"
                      >
                        <span className="text-2xl">👤</span>
                        <div className="flex-1">
                          <p className="text-gray-900 font-bold text-base">Worked By Myself</p>
                          <p className="text-xs text-gray-600">Tap if you worked alone — no one else on the job</p>
                        </div>
                        <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center ${manualEntry.worked_alone ? 'bg-blue-600 border-blue-600' : 'border-gray-400'}`}>
                          {manualEntry.worked_alone && <span className="text-white text-sm font-bold">✓</span>}
                        </div>
                      </div>
                      {!manualEntry.worked_alone && (
                      <div className="max-h-40 overflow-y-auto border-2 border-gray-400 rounded-lg p-3 space-y-2 bg-gray-50">
                        {teamMembers.map((member) => (
                          <div key={member.id} className="flex items-center space-x-3 py-1">
                            <Checkbox
                              id={`member-${member.id}`}
                              checked={manualEntry.worked_with.includes(member.id)}
                              onCheckedChange={() => toggleWorkedWith(member.id)}
                              className="w-5 h-5"
                            />
                            <label htmlFor={`member-${member.id}`} className="text-base text-gray-900 font-semibold cursor-pointer">
                              {member.name}
                            </label>
                          </div>
                        ))}
                      </div>
                      )}
                    </div>
                  )}
                  <div
                    onClick={() => setManualEntry((prev) => ({ ...prev, no_lunch: !prev.no_lunch }))}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition ${
                      manualEntry.no_lunch ? 'border-orange-500 bg-orange-50' : 'border-gray-300 bg-white'
                    }`}
                    data-testid="no-lunch-toggle"
                  >
                    <span className="text-2xl">🚫🍽️</span>
                    <div className="flex-1">
                      <p className="text-gray-900 font-bold text-base">No Lunch Taken</p>
                      <p className="text-xs text-gray-600">Tap if you worked through lunch so the employer knows</p>
                    </div>
                    <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center ${manualEntry.no_lunch ? 'bg-orange-500 border-orange-500' : 'border-gray-400'}`}>
                      {manualEntry.no_lunch && <span className="text-white text-sm font-bold">✓</span>}
                    </div>
                  </div>

                  <div>
                    <Label className="text-gray-900 font-bold text-base mb-2 block">Notes</Label>
                    <VoiceTextarea
                      value={manualEntry.notes}
                      onChange={(text) => setManualEntry((prev) => ({ ...prev, notes: text }))}
                      placeholder="Add notes… or tap the mic and tell your phone what you did"
                      rows={8}
                      className="mt-1 text-gray-900 font-semibold border-2 border-gray-400 text-base min-h-[180px]"
                      testid="entry-notes"
                    />
                  </div>
                </div>
                <div className="flex-shrink-0 pt-4 border-t-2 border-gray-300 mt-4">
                  <Button onClick={handleSaveEntry} className="w-full bg-red-600 hover:bg-red-700 text-white font-bold text-lg min-h-[52px] shadow-md">
                    {editingLog ? 'Update' : 'Add'} Entry
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Rain Day Button and Dialog */}
            <Dialog open={isRainDayOpen} onOpenChange={setIsRainDayOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-gray-600 hover:bg-gray-700 text-white font-bold shadow-md min-h-[48px] px-3 sm:px-4 text-sm sm:text-base"
                  onClick={() => {
                    setRainDayEntry({
                      date: todayET(),
                      project_id: '',
                      notes: ''
                    });
                  }}
                >
                  <CloudRain className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="whitespace-nowrap">Rain Day</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white text-gray-900 border-2 border-gray-400 max-w-lg w-[95vw] max-h-[85vh] overflow-hidden flex flex-col shadow-xl">
                <DialogHeader className="flex-shrink-0 pb-3 border-b-2 border-gray-200">
                  <DialogTitle className="text-gray-900 text-2xl font-bold">Add Rain Day</DialogTitle>
                </DialogHeader>
                <div className="space-y-5 overflow-y-auto flex-1 pr-2 pt-4">
                  <div>
                    <Label className="text-gray-900 font-bold text-base mb-2 block">Date</Label>
                    <Input
                      type="date"
                      value={rainDayEntry.date}
                      onChange={(e) => setRainDayEntry({ ...rainDayEntry, date: e.target.value })}
                      required
                      className="w-full text-gray-900 font-semibold border-2 border-gray-400 text-base h-12"
                    />
                  </div>

                  <div>
                    <Label className="text-gray-900 font-bold text-base mb-2 block">Project (Optional)</Label>
                    <Select
                      value={rainDayEntry.project_id}
                      onValueChange={(value) => setRainDayEntry({ ...rainDayEntry, project_id: value })}
                    >
                      <SelectTrigger className="text-gray-900 font-semibold border-2 border-gray-400 h-12 text-base">
                        <SelectValue placeholder="Select project (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Project</SelectItem>
                        {projects.map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-gray-900 font-bold text-base mb-2 block">Notes (Optional)</Label>
                    <Textarea
                      value={rainDayEntry.notes}
                      onChange={(e) => setRainDayEntry({ ...rainDayEntry, notes: e.target.value })}
                      placeholder="Weather details, reason for no work..."
                      className="mt-1 text-gray-900 font-semibold border-2 border-gray-400 text-base"
                      rows={3}
                    />
                  </div>
                </div>
                <div className="flex-shrink-0 pt-4 border-t-2 border-gray-300 mt-4">
                  <Button onClick={handleSaveRainDay} className="w-full bg-gray-700 hover:bg-gray-800 text-white font-bold text-lg min-h-[52px] shadow-md">
                    Add Rain Day
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Snow Day Button and Dialog */}
            <Dialog open={isSnowDayOpen} onOpenChange={setIsSnowDayOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-md min-h-[48px] px-3 sm:px-4 text-sm sm:text-base"
                  onClick={() => {
                    setSnowDayEntry({
                      date: todayET(),
                      project_id: '',
                      notes: ''
                    });
                  }}
                >
                  <CloudSnow className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="whitespace-nowrap">Snow Day</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white text-gray-900 border-2 border-gray-400 max-w-lg w-[95vw] max-h-[85vh] overflow-hidden flex flex-col shadow-xl">
                <DialogHeader className="flex-shrink-0 pb-3 border-b-2 border-gray-200">
                  <DialogTitle className="text-gray-900 text-2xl font-bold">Add Snow Day</DialogTitle>
                </DialogHeader>
                <div className="space-y-5 overflow-y-auto flex-1 pr-2 pt-4">
                  <div>
                    <Label className="text-gray-900 font-bold text-base mb-2 block">Date</Label>
                    <Input
                      type="date"
                      value={snowDayEntry.date}
                      onChange={(e) => setSnowDayEntry({ ...snowDayEntry, date: e.target.value })}
                      required
                      className="w-full text-gray-900 font-semibold border-2 border-gray-400 text-base h-12"
                    />
                  </div>

                  <div>
                    <Label className="text-gray-900 font-bold text-base mb-2 block">Project (Optional)</Label>
                    <Select
                      value={snowDayEntry.project_id}
                      onValueChange={(value) => setSnowDayEntry({ ...snowDayEntry, project_id: value })}
                    >
                      <SelectTrigger className="text-gray-900 font-semibold border-2 border-gray-400 h-12 text-base">
                        <SelectValue placeholder="Select project (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Project</SelectItem>
                        {projects.map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-gray-900 font-bold text-base mb-2 block">Notes (Optional)</Label>
                    <Textarea
                      value={snowDayEntry.notes}
                      onChange={(e) => setSnowDayEntry({ ...snowDayEntry, notes: e.target.value })}
                      placeholder="Weather details, reason for no work..."
                      className="mt-1 text-gray-900 font-semibold border-2 border-gray-400 text-base"
                      rows={3}
                    />
                  </div>
                </div>
                <div className="flex-shrink-0 pt-4 border-t-2 border-gray-300 mt-4">
                  <Button onClick={handleSaveSnowDay} className="w-full bg-blue-700 hover:bg-blue-800 text-white font-bold text-lg min-h-[52px] shadow-md">
                    Add Snow Day
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Holiday Button and Dialog */}
            <Dialog open={isHolidayOpen} onOpenChange={setIsHolidayOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-teal-600 hover:bg-teal-700 text-white font-bold shadow-md min-h-[48px] px-3 sm:px-4 text-sm sm:text-base"
                  onClick={() => {
                    setHolidayEntry({
                      date: todayET(),
                      project_id: '',
                      notes: ''
                    });
                  }}
                  data-testid="holiday-btn"
                >
                  <Umbrella className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="whitespace-nowrap">Holiday</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white text-gray-900 border-2 border-gray-400 max-w-lg w-[95vw] max-h-[85vh] overflow-hidden flex flex-col shadow-xl">
                <DialogHeader className="flex-shrink-0 pb-3 border-b-2 border-gray-200">
                  <DialogTitle className="text-gray-900 text-2xl font-bold">Add Holiday</DialogTitle>
                </DialogHeader>
                <div className="space-y-5 overflow-y-auto flex-1 pr-2 pt-4">
                  <div>
                    <Label className="text-gray-900 font-bold text-base mb-2 block">Date</Label>
                    <Input
                      type="date"
                      value={holidayEntry.date}
                      onChange={(e) => setHolidayEntry({ ...holidayEntry, date: e.target.value })}
                      required
                      className="w-full text-gray-900 font-semibold border-2 border-gray-400 text-base h-12"
                      data-testid="holiday-date-input"
                    />
                  </div>

                  <div>
                    <Label className="text-gray-900 font-bold text-base mb-2 block">Notes (Optional)</Label>
                    <Textarea
                      value={holidayEntry.notes}
                      onChange={(e) => setHolidayEntry({ ...holidayEntry, notes: e.target.value })}
                      placeholder="Vacation, personal day..."
                      className="mt-1 text-gray-900 font-semibold border-2 border-gray-400 text-base"
                      rows={3}
                    />
                  </div>
                </div>
                <div className="flex-shrink-0 pt-4 border-t-2 border-gray-300 mt-4">
                  <Button onClick={handleSaveHoliday} className="w-full bg-teal-700 hover:bg-teal-800 text-white font-bold text-lg min-h-[52px] shadow-md" data-testid="save-holiday-btn">
                    Add Holiday
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Stat Holiday Button and Dialog */}
            <Dialog open={isStatHolidayOpen} onOpenChange={setIsStatHolidayOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-amber-500 hover:bg-amber-600 text-white font-bold shadow-md min-h-[48px] px-3 sm:px-4 text-sm sm:text-base"
                  onClick={() => {
                    setStatHolidayEntry({
                      date: todayET(),
                      project_id: '',
                      notes: ''
                    });
                  }}
                  data-testid="stat-holiday-btn"
                >
                  <Sun className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="whitespace-nowrap">Stat Holiday</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white text-gray-900 border-2 border-gray-400 max-w-lg w-[95vw] max-h-[85vh] overflow-hidden flex flex-col shadow-xl">
                <DialogHeader className="flex-shrink-0 pb-3 border-b-2 border-gray-200">
                  <DialogTitle className="text-gray-900 text-2xl font-bold">Add Stat Holiday</DialogTitle>
                </DialogHeader>
                <div className="space-y-5 overflow-y-auto flex-1 pr-2 pt-4">
                  <div>
                    <Label className="text-gray-900 font-bold text-base mb-2 block">Date</Label>
                    <Input
                      type="date"
                      value={statHolidayEntry.date}
                      onChange={(e) => setStatHolidayEntry({ ...statHolidayEntry, date: e.target.value })}
                      required
                      className="w-full text-gray-900 font-semibold border-2 border-gray-400 text-base h-12"
                      data-testid="stat-holiday-date"
                    />
                  </div>

                  <div>
                    <Label className="text-gray-900 font-bold text-base mb-2 block">Project (Optional)</Label>
                    <Select
                      value={statHolidayEntry.project_id}
                      onValueChange={(value) => setStatHolidayEntry({ ...statHolidayEntry, project_id: value })}
                    >
                      <SelectTrigger className="text-gray-900 font-semibold border-2 border-gray-400 h-12 text-base">
                        <SelectValue placeholder="Select project (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Project</SelectItem>
                        {projects.map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-gray-900 font-bold text-base mb-2 block">Holiday Name / Notes (Optional)</Label>
                    <Textarea
                      value={statHolidayEntry.notes}
                      onChange={(e) => setStatHolidayEntry({ ...statHolidayEntry, notes: e.target.value })}
                      placeholder="e.g. Canada Day, Labour Day…"
                      className="mt-1 text-gray-900 font-semibold border-2 border-gray-400 text-base"
                      rows={3}
                    />
                  </div>
                </div>
                <div className="flex-shrink-0 pt-4 border-t-2 border-gray-300 mt-4">
                  <Button onClick={handleSaveStatHoliday} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold text-lg min-h-[52px] shadow-md" data-testid="save-stat-holiday-btn">
                    Add Stat Holiday
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Weekly Hours Reminder button */}
            <Button
              onClick={() => setIsReminderOpen(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white font-bold shadow-md min-h-[48px] px-3 sm:px-4 text-sm sm:text-base"
              data-testid="weekly-reminder-btn"
            >
              <Bell className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="whitespace-nowrap">Email Reminder</span>
            </Button>
            <WeeklyHoursReminderDialog open={isReminderOpen} onOpenChange={setIsReminderOpen} />

            {/* New Client suggestion dialog */}
            <Dialog open={newClientDialogOpen} onOpenChange={setNewClientDialogOpen}>
              <DialogContent className="max-w-md" data-testid="new-client-dialog">
                <DialogHeader>
                  <DialogTitle className="text-lg font-bold text-gray-900">Suggest a New Client</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <p className="text-xs text-gray-600">
                    Don't see your client in the list? Type their info below. Walter will get an email and approve before it shows for everyone.
                  </p>
                  <div>
                    <Label className="text-gray-900 font-bold text-sm">Client name *</Label>
                    <Input
                      type="text"
                      value={newClientForm.name}
                      onChange={(e) => setNewClientForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g. Jane Smith"
                      className="text-gray-900 font-semibold border-2 border-gray-400 h-11 mt-1"
                      data-testid="new-client-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-900 font-bold text-sm">Company (optional)</Label>
                    <Input
                      type="text"
                      value={newClientForm.company}
                      onChange={(e) => setNewClientForm(prev => ({ ...prev, company: e.target.value }))}
                      placeholder="e.g. Smith &amp; Co."
                      className="text-gray-900 font-semibold border-2 border-gray-400 h-11 mt-1"
                      data-testid="new-client-company-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-900 font-bold text-sm">Phone (optional)</Label>
                    <Input
                      type="tel"
                      value={newClientForm.phone}
                      onChange={(e) => setNewClientForm(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="e.g. (902) 555-1234"
                      className="text-gray-900 font-semibold border-2 border-gray-400 h-11 mt-1"
                      data-testid="new-client-phone-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-900 font-bold text-sm">Note for admin (optional)</Label>
                    <Input
                      type="text"
                      value={newClientForm.note}
                      onChange={(e) => setNewClientForm(prev => ({ ...prev, note: e.target.value }))}
                      placeholder="Any context for Walter…"
                      className="text-gray-900 font-semibold border-2 border-gray-400 h-11 mt-1"
                      data-testid="new-client-note-input"
                    />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setNewClientDialogOpen(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={submitNewClientDialog}
                      disabled={submittingClient || !newClientForm.name.trim()}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold"
                      data-testid="submit-new-client-btn"
                    >
                      {submittingClient ? 'Submitting…' : 'Send for approval'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            </div>
          </div>
          
          {/* Search Bar */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input
              type="text"
              placeholder="Search entries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white border-2 border-gray-400 text-gray-900 font-semibold placeholder:text-gray-500 h-12 text-base"
            />
          </div>

          {/* Big Add Entry button (right under search) */}
          <Button
            onClick={() => {
              setEditingLog(null);
              const today = todayET();
              setManualEntry({
                clock_in: `${today}T07:00`,
                clock_out: `${today}T17:00`,
                project_id: '',
                service_id: '',
                client_id: '',
                other_client: '',
                notes: '',
                no_lunch: false,
                worked_alone: false,
                worked_with: [],
                barge_type: 'S',
                location: '',
                fuel_added: '',
                ramp_used: '',
                operating_time: '',
                standby_time: '',
                platform_time: '',
                equipment_skidsteer_hrs: '',
                equipment_excavator_hrs: '',
                equipment_other_hrs: '',
                equipment_other_name: '',
                load_description: '',
                persons_on_barge: ''
              });
              setIsManualEntryOpen(true);
            }}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg min-h-[56px] shadow-md mb-2"
            data-testid="big-add-entry-btn"
          >
            <Plus className="w-6 h-6 mr-2" />
            Add Entry
          </Button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="w-full px-4 py-4 bg-white border-b-2 border-gray-300 shadow-sm">
        {/* Admin View Toggle */}
        {(user.role === 'admin' || user.role === 'supervisor') && (
          <div className="space-y-3 mb-4">
            <div className="flex gap-2">
              <button
                onClick={() => setAdminView('my')}
                className={`px-4 py-2 rounded-lg font-bold text-sm transition-all ${
                  adminView === 'my'
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                My Hours
              </button>
              <button
                onClick={() => {
                  setAdminView('all');
                  setViewMode('all'); // Switch to 'All' time view to see all employees
                }}
                className={`px-4 py-2 rounded-lg font-bold text-sm transition-all ${
                  adminView === 'all'
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                All Employee Hours
              </button>
            </div>

            {/* Date Range Filter - Only show when viewing all employees */}
            {adminView === 'all' && (
              <div className="bg-gradient-to-r from-green-50 to-blue-50 p-3 rounded-lg border-2 border-green-200">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <label className="text-sm font-bold text-gray-900 whitespace-nowrap">
                    📅 Filter by Date Range:
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2 flex-1">
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-semibold text-gray-700">From:</label>
                      <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="border-2 border-gray-400 text-gray-900 font-semibold h-10 text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-semibold text-gray-700">To:</label>
                      <Input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="border-2 border-gray-400 text-gray-900 font-semibold h-10 text-sm"
                      />
                    </div>
                    {(startDate || endDate) && (
                      <button
                        onClick={() => {
                          setStartDate('');
                          setEndDate('');
                        }}
                        className="px-3 py-2 bg-red-500 hover:bg-red-600 text-white text-xs font-bold rounded-lg transition-all whitespace-nowrap"
                      >
                        Clear Dates
                      </button>
                    )}
                  </div>
                </div>
                {startDate && endDate && (
                  <div className="mt-2 text-xs text-gray-700 font-semibold">
                    📊 Showing hours from {new Date(startDate).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })} to {new Date(endDate).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Employee Summary and Filters - Only show when viewing all employees */}
        {(user.role === 'admin' || user.role === 'supervisor') && adminView === 'all' && (
          <div className="space-y-4 mb-4">
            {/* Employee Summary Cards */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border-2 border-blue-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  Employee Hours Summary
                </h3>
                {viewMode !== 'all' && employeeSummaries.length < 3 && (
                  <span className="text-xs text-gray-600 bg-yellow-100 px-2 py-1 rounded border border-yellow-300">
                    📅 Viewing {viewMode} only
                  </span>
                )}
              </div>
              {employeeSummaries.length === 0 ? (
                <div className="bg-white p-6 rounded-lg border-2 border-gray-300 text-center">
                  <Info className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-900 font-semibold mb-1">No employees with logs in this time period</p>
                  <p className="text-sm text-gray-600">Try switching to "All" view to see all employee data</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {employeeSummaries.map(summary => (
                    <div 
                      key={summary.user_id}
                      className="bg-white p-3 rounded-lg border-2 border-gray-300 hover:border-blue-400 transition-all cursor-pointer"
                      onClick={() => setSelectedEmployeeId(summary.user_id)}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-bold text-gray-900">{summary.user_name}</p>
                          <p className="text-sm text-gray-600">{summary.entries_count} entries</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-blue-600">{summary.total_hours.toFixed(1)}</p>
                          <p className="text-xs text-gray-500">hours</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Filter and Sort Controls */}
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Employee Dropdown */}
              <div className="flex-1">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Filter by Employee
                </label>
                <select
                  value={selectedEmployeeId}
                  onChange={(e) => setSelectedEmployeeId(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-gray-400 rounded-lg font-semibold text-gray-900 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                >
                  <option value="all">All Employees ({employeeSummaries.length})</option>
                  {employeeSummaries.map(summary => (
                    <option key={summary.user_id} value={summary.user_id}>
                      {summary.user_name} ({summary.total_hours.toFixed(1)}h)
                    </option>
                  ))}
                </select>
              </div>

              {/* Sort By Dropdown */}
              <div className="flex-1">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Sort By
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-gray-400 rounded-lg font-semibold text-gray-900 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                >
                  <option value="recent">Most Recent First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="client">By Client Name</option>
                </select>
              </div>
            </div>

            {/* Selected Employee Info */}
            {selectedEmployeeId !== 'all' && (
              <div className="bg-blue-600 text-white p-3 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  <span className="font-bold">
                    Viewing: {employeeSummaries.find(s => s.user_id === selectedEmployeeId)?.user_name}
                  </span>
                </div>
                <button
                  onClick={() => setSelectedEmployeeId('all')}
                  className="bg-white text-blue-600 px-3 py-1 rounded font-semibold hover:bg-blue-50 transition-colors"
                >
                  Clear Filter
                </button>
              </div>
            )}
          </div>
        )}
        
        <div className="grid grid-cols-4 gap-2 sm:flex sm:gap-3 mb-4">
          {['day', 'week', 'month', 'all'].map((mode) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-3 sm:px-6 py-3 rounded-lg font-bold text-sm sm:text-base min-h-[48px] transition-all ${
                viewMode === mode
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-white text-gray-900 border-2 border-gray-400 hover:bg-gray-50 hover:border-blue-500'
              }`}
            >
              {mode.charAt(0).toUpperCase() + mode.slice(1)}
            </button>
          ))}
        </div>

        {/* Export Controls */}
        {filteredLogs.length > 0 && (
          <div className="flex items-center gap-2 sm:gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-2 bg-gray-50 px-3 sm:px-4 py-2 rounded-lg border-2 border-gray-300 min-h-[48px]">
              <Checkbox
                id="select-all"
                checked={selectedForExport.length === filteredLogs.length && filteredLogs.length > 0}
                onCheckedChange={toggleSelectAll}
                className="w-6 h-6"
              />
              <label htmlFor="select-all" className="text-sm sm:text-base font-semibold text-gray-900 cursor-pointer whitespace-nowrap">
                Select All ({filteredLogs.length})
              </label>
            </div>
            <Button
              onClick={handleExportToEmail}
              disabled={selectedForExport.length === 0}
              className="bg-green-600 hover:bg-green-700 text-white font-bold min-h-[48px] px-4 sm:px-6 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base flex-1 sm:flex-initial"
            >
              <Calendar className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
              <span className="truncate">Export ({selectedForExport.length})</span>
            </Button>
          </div>
        )}

        {/* Total Hours */}
        <div className="bg-white border-2 border-blue-600 rounded-lg p-6 shadow-lg">
          <div className="text-center">
            <p className="text-gray-900 font-bold text-lg mb-2">Total Hours</p>
            <p className="text-blue-600 font-black text-6xl">{calculateTotalHours(filteredLogs)}h</p>
          </div>
        </div>
      </div>

      {/* Entries List */}
      <div className="w-full px-4 py-6 bg-gray-100">
        {Object.keys(groupedLogs).length === 0 ? (
          <div className="bg-white border-2 border-gray-300 rounded-lg p-12 text-center shadow-sm">
            <Clock className="w-20 h-20 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-900 font-bold text-xl">No entries found</p>
          </div>
        ) : (
          <div className="space-y-5">
            {isEmployeeView ? (
              /* Employee View - Group by Employee */
              Object.entries(groupedLogs).map(([employeeName, employeeData]) => (
                <div key={employeeName}>
                  {/* Employee Header */}
                  <div className="bg-orange-500 rounded-lg px-4 py-3 mb-3 shadow-sm">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-bold text-white">{employeeName}</h3>
                      <span className="text-base font-bold text-white">Total: {employeeData.totalHours.toFixed(2)}h</span>
                    </div>
                  </div>

                  {/* Entry Cards */}
                  <div className="space-y-3">
                    {employeeData.logs.map((log) => (
                      <div
                        key={log.id}
                        className={`border-2 rounded-lg p-3 sm:p-4 hover:shadow-lg transition-all ${
                          log.is_rain_day
                            ? 'bg-gray-100 border-gray-400'
                            : selectedForExport.includes(log.id) 
                              ? 'bg-green-50 border-green-500' 
                              : 'bg-white border-gray-300 hover:border-blue-600'
                        }`}
                      >
                        <div className="flex items-start gap-2 sm:gap-3">
                          {/* Checkbox for export selection */}
                          <div className="pt-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              id={`export-${log.id}`}
                              checked={selectedForExport.includes(log.id)}
                              onCheckedChange={() => toggleSelectForExport(log.id)}
                              className="w-6 h-6 sm:w-7 sm:h-7 border-2"
                            />
                          </div>

                          {/* Entry Content */}
                          <div 
                            className="flex-1 cursor-pointer"
                            onClick={() => setSelectedEntry(selectedEntry?.id === log.id ? null : log)}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                {log.is_rain_day ? (
                                  <CloudRain className="w-5 h-5 text-gray-600" />
                                ) : log.is_snow_day ? (
                                  <CloudSnow className="w-5 h-5 text-blue-500" />
                                ) : log.is_stat_holiday ? (
                                  <Sun className="w-5 h-5 text-amber-500" />
                                ) : log.is_holiday ? (
                                  <Umbrella className="w-5 h-5 text-teal-600" />
                                ) : (
                                  <Clock className="w-5 h-5 text-blue-600" />
                                )}
                                <span className="text-gray-900 font-bold text-base">
                                  {log.is_rain_day ? 'Rain Day' : log.is_snow_day ? 'Snow Day' : log.is_stat_holiday ? 'Stat Holiday' : log.is_holiday ? 'Holiday' : `${formatTime(log.clock_in)} - ${log.clock_out ? formatTime(log.clock_out) : 'In Progress'}`}
                                </span>
                              </div>
                              <span className="text-blue-600 font-black text-xl">{log.hours?.toFixed(2) || 0}h</span>
                            </div>
                            
                            <div className="text-sm text-gray-700">
                              <p className="font-semibold">📅 {formatDate(log.clock_in)}</p>
                              {log.client_id && (() => {
                                const client = clients.find(c => c.id === log.client_id);
                                return client ? <p className="font-bold text-orange-600">👤 {client.name}</p> : null;
                              })()}
                              {log.project_id ? (
                                <p className="font-medium">📁 {getProjectName(log.project_id)}</p>
                              ) : log.service_type ? (
                                <p className="font-medium">🔧 {log.service_type}</p>
                              ) : log.service_id && (() => {
                                const service = services.find(s => s.id === log.service_id);
                                return service ? <p className="font-medium">🔧 {service.service_type}</p> : null;
                              })()}
                              {log.no_lunch && <p className="font-bold text-orange-600" data-testid={`admin-no-lunch-${log.id}`}>🚫🍽️ No Lunch Taken</p>}
                              {log.worked_alone && <p className="font-bold text-blue-600" data-testid={`admin-worked-alone-${log.id}`}>👤 Worked Alone</p>}
                              {log.notes && <p className="text-gray-600 mt-1">📝 {log.notes}</p>}
                            </div>

                            {selectedEntry?.id === log.id && !log.is_rain_day && log.worked_with && log.worked_with.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <p className="text-sm font-semibold text-gray-700 mb-2">Worked With:</p>
                                <div className="flex flex-wrap gap-2">
                                  {log.worked_with.map((memberId, idx) => {
                                    const member = teamMembers.find(m => m.id === memberId);
                                    return member ? (
                                      <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-900 rounded text-sm font-medium">
                                        {member.name}
                                      </span>
                                    ) : null;
                                  })}
                                </div>
                              </div>
                            )}

                            {selectedEntry?.id === log.id && (
                              <div className="mt-3 pt-3 border-t border-gray-200 flex gap-2">
                                {!log.is_rain_day && (
                                  <Button
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingLog(log);
                                      setManualEntry({
                                        clock_in: toETInputValue(log.clock_in),
                                        clock_out: log.clock_out ? toETInputValue(log.clock_out) : '',
                                        project_id: log.project_id || 'none',
                                        service_id: log.service_id || '',
                                        service_type: log.service_type || '',
                                        client_id: log.client_id || '',
                                        other_client: log.other_client || '',
                                        notes: log.notes || '',
                                        no_lunch: !!log.no_lunch,
                                        worked_alone: !!log.worked_alone,
                                        worked_with: log.worked_with || []
                                      });
                                      setIsManualEntryOpen(true);
                                    }}
                                    className="flex-1 bg-blue-600 text-white font-semibold hover:bg-blue-700 min-h-[44px]"
                                  >
                                    <Edit2 className="w-4 h-4 mr-1" />
                                    Edit
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteLog(log.id);
                                  }}
                                  className={`${log.is_rain_day ? 'w-full' : 'flex-1'} bg-red-600 text-white font-semibold hover:bg-red-700 min-h-[44px]`}
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Delete
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              /* Date View - Group by Date */
              Object.entries(groupedLogs)
                .sort(([dateA, logsA], [dateB, logsB]) => {
                  // Sort date groups by newest first
                  const firstLogA = logsA[0];
                  const firstLogB = logsB[0];
                  if (!firstLogA || !firstLogB) return 0;
                  return new Date(firstLogB.clock_in) - new Date(firstLogA.clock_in);
                })
                .map(([date, logs]) => (
                <div key={date}>
                  {/* Date Header */}
                  <div className="bg-blue-600 rounded-lg px-4 py-3 mb-3 shadow-sm">
                    <div className="flex justify-between items-center">
                      <h3 className="text-base font-bold text-white uppercase">{date}</h3>
                      <span className="text-base font-bold text-white">{calculateTotalHours(logs)}h</span>
                    </div>
                  </div>

                  {/* Entry Cards */}
                  <div className="space-y-3">
                    {logs.map((log) => (
                    <div
                      key={log.id}
                      className={`border-2 rounded-lg p-3 sm:p-4 hover:shadow-lg transition-all ${
                        log.is_rain_day
                          ? 'bg-gray-100 border-gray-400'
                          : selectedForExport.includes(log.id) 
                            ? 'bg-green-50 border-green-500' 
                            : 'bg-white border-gray-300 hover:border-blue-600'
                      }`}
                    >
                      <div className="flex items-start gap-2 sm:gap-3">
                        {/* Checkbox for export selection */}
                        <div className="pt-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            id={`export-${log.id}`}
                            checked={selectedForExport.includes(log.id)}
                            onCheckedChange={() => toggleSelectForExport(log.id)}
                            className="w-6 h-6 sm:w-7 sm:h-7 border-2"
                          />
                        </div>

                        {/* Entry Content */}
                        <div 
                          className="flex-1 cursor-pointer"
                          onClick={() => setSelectedEntry(selectedEntry?.id === log.id ? null : log)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  {log.is_rain_day && <CloudRain className="w-5 h-5 text-gray-600" />}
                                  {log.is_snow_day && <CloudSnow className="w-5 h-5 text-blue-500" />}
                                  {log.is_stat_holiday && <Sun className="w-5 h-5 text-amber-500" />}
                                  {log.is_holiday && <Umbrella className="w-5 h-5 text-teal-600" />}
                                  <h4 className={`text-lg font-bold ${log.is_rain_day ? 'text-gray-700' : 'text-gray-900'}`}>
                                    {log.is_rain_day ? 'Rain Day' : log.is_snow_day ? 'Snow Day' : log.is_stat_holiday ? 'Stat Holiday' : log.is_holiday ? 'Holiday' : getEntryTitle(log)}
                                  </h4>
                                </div>
                                <span className={`font-bold text-lg ml-3 ${log.is_rain_day ? 'text-gray-600' : 'text-blue-600'}`}>
                                  {log.hours}h
                                </span>
                              </div>
                              {!log.is_rain_day && (
                                <p className="text-base text-gray-700 font-medium">
                                  {formatTime(log.clock_in)} - {formatTime(log.clock_out)}
                                </p>
                              )}
                              {!log.is_rain_day && (
                                <>
                                  {log.project_id && (
                                    <p className="text-sm text-gray-600 font-medium">
                                      📁 Project: {getProjectName(log.project_id)}
                                    </p>
                                  )}
                                  {!log.project_id && getLogClientName(log) && (
                                    <p className="text-sm text-gray-600 font-medium" data-testid={`entry-client-${log.id}`}>
                                      👤 Client: {getLogClientName(log)}
                                    </p>
                                  )}
                                  {!log.project_id && getLogServiceName(log) && (
                                    <p className="text-sm text-gray-600 font-medium" data-testid={`entry-service-${log.id}`}>
                                      🔧 Service: {getLogServiceName(log)}
                                    </p>
                                  )}
                                  {log.no_lunch && (
                                    <p className="text-sm font-bold text-orange-600" data-testid={`entry-no-lunch-${log.id}`}>
                                      🚫🍽️ No Lunch Taken
                                    </p>
                                  )}
                                  {log.worked_alone && (
                                    <p className="text-sm font-bold text-blue-600" data-testid={`entry-worked-alone-${log.id}`}>
                                      👤 Worked Alone
                                    </p>
                                  )}
                                </>
                              )}
                              {log.is_rain_day && log.project_id && (
                                <p className="text-sm text-gray-600 font-medium">
                                  Project: {getProjectName(log.project_id)}
                                </p>
                              )}
                            </div>
                            <div className="ml-4">
                              <Info className={`w-6 h-6 ${log.is_rain_day ? 'text-gray-600' : 'text-blue-600'}`} />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {selectedEntry?.id === log.id && (
                        <div className="mt-4 pt-4 border-t-2 border-gray-200 space-y-3 bg-gray-50 rounded-b-lg ml-9">
                          {log.notes && (
                            <div className="pt-2">
                              <p className="text-sm text-gray-600 font-semibold mb-1">Notes</p>
                              <p className="text-base text-gray-900">{log.notes}</p>
                            </div>
                          )}
                          {!log.is_rain_day && log.worked_with && log.worked_with.length > 0 && (
                            <div>
                              <p className="text-sm text-gray-600 font-semibold mb-2">Worked With</p>
                              <div className="flex flex-wrap gap-2">
                                {log.worked_with.map(memberId => {
                                  const member = teamMembers.find(m => m.id === memberId);
                                  return member ? (
                                    <span key={memberId} className="px-3 py-1.5 bg-blue-100 text-blue-900 text-sm font-semibold rounded-lg border border-blue-300">
                                      {member.name}
                                    </span>
                                  ) : null;
                                })}
                              </div>
                            </div>
                          )}
                          <div className="flex gap-2 pt-2">
                            {!log.is_rain_day && (
                              <Button
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditLog(log);
                                }}
                                className="flex-1 bg-blue-600 text-white font-semibold hover:bg-blue-700 min-h-[44px]"
                              >
                                <Edit2 className="w-4 h-4 mr-1" />
                                Edit
                              </Button>
                            )}
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteLog(log.id);
                              }}
                              className={`${log.is_rain_day ? 'w-full' : 'flex-1'} bg-red-600 text-white font-semibold hover:bg-red-700 min-h-[44px]`}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeClock;
